import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Input, message, Modal, Select, Space, Switch } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Integration, IntegrationMapping } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import './integration-mappings-page.scoped.scss';
import TextArea from 'antd/es/input/TextArea';

function DeleteButton(props: { mapping?: IntegrationMapping; onSaved?: () => void }) {
  const [loading, setLoading] = useState(false);

  const deleteMapping = () => {
    setLoading(true);
    apiClient
      .delete(`mappings/${props.mapping?.id}`)
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onDeleteClick = () => {
    Modal.confirm({
      title: 'Eşleştirme sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Eşleştirmeyi silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: deleteMapping,
    });
  };

  return (
    <Button icon={<CloseOutlined />} onClick={onDeleteClick} loading={loading}>
      Sil
    </Button>
  );
}

function IgnoreTasksSwitch(props: { mapping?: IntegrationMapping; onSaved?: () => void }) {
  const [loading, setLoading] = useState(false);

  const onSwitchChange = (ignoreTasks: boolean) => {
    setLoading(true);
    apiClient
      .patch(`mappings/${props.mapping?.id}`, { ignoreTasks, ignoreErrors: props.mapping?.ignoreErrors })
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Space>
      <Switch checked={props.mapping?.ignoreTasks} loading={loading} onChange={onSwitchChange} />
      <span>Görevleri yok say</span>
    </Space>
  );
}

function IgnoreErrorsSwitch(props: { mapping?: IntegrationMapping; onSaved?: () => void }) {
  const [loading, setLoading] = useState(false);

  const onSwitchChange = (ignoreErrors: boolean) => {
    setLoading(true);
    apiClient
      .patch(`mappings/${props.mapping?.id}`, { ignoreErrors, ignoreTasks: props.mapping?.ignoreTasks })
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Space>
      <Switch checked={props.mapping?.ignoreErrors} loading={loading} onChange={onSwitchChange} />
      <span>Kural hatalarını yok say</span>
    </Space>
  );
}

function CommentBox(props: { mapping?: IntegrationMapping; onSaved?: () => void }) {
  const [comment, setComment] = useState(props.mapping?.comment);
  const [loading, setLoading] = useState(false);

  const saveComment = () => {
    setLoading(true);
    apiClient
      .patch(`mappings/${props.mapping?.id}/comment`, { comment })
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Space>
      <TextArea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Entegrasyon adresi"
        style={{ width: '20rem' }}
        maxLength={4000}
      />
      <Button type="primary" onClick={saveComment} loading={loading}>
        Kaydet
      </Button>
    </Space>
  );
}

function NewMappingForm(props: { integrationId?: string; fieldOptions: any[]; mappings?: IntegrationMapping[]; onSaved?: () => void }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = (values: any) => {
    if (loading) {
      return;
    }

    const columnExists = props.mappings?.some(m => m.columnName.toLowerCase() === values.columnName.toLowerCase());
    if (columnExists) {
      message.error('Bu alan ismi ile daha önce bir eşleştirme yapılmış. Lütfen farklı bir alan ismi giriniz.');
      return;
    }

    const body = {
      integrationId: props.integrationId,
      columnName: values.columnName,
      fieldId: values.fieldId,
      ignoreTasks: values.ignoreTasks,
      ignoreErrors: values.ignoreErrors,
      comment: values.comment,
    };
    setLoading(true);
    apiClient
      .post('mappings', body)
      .then(() => {
        if (props.onSaved) {
          props.onSaved();
        }
        form.resetFields();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form form={form} layout="inline" style={{ marginTop: '2rem' }} onFinish={onFinish}>
      <table style={{ width: '100%' }}>
        <tbody>
          <tr className="mapping-row">
            <td style={{ width: '30%' }}>
              <Form.Item name="columnName" style={{ margin: 0 }} rules={[{ required: true, message: 'Zorunlu alan' }]}>
                <Input placeholder="Alan ismi" />
              </Form.Item>
            </td>
            <td style={{ width: '30%' }}>
              <Form.Item name="fieldId" style={{ margin: 0 }} rules={[{ required: true, message: 'Zorunlu alan' }]}>
                <Select
                  placeholder="Veri elemanı"
                  showSearch
                  style={{ width: '100%' }}
                  filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                  {props.fieldOptions?.map(o => (
                    <Select.Option key={o.value} value={o.value}>
                      {o.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </td>
            <td style={{ width: '15%' }}>
              <Form.Item name="ignoreTasks" valuePropName="checked">
                <Checkbox>Görevleri yok say</Checkbox>
              </Form.Item>
              <Form.Item name="ignoreErrors" valuePropName="checked">
                <Checkbox>Kural hatalarını yok say</Checkbox>
              </Form.Item>
            </td>
            <td style={{ width: '35%' }}>
              <Form.Item name="comment" style={{ margin: 0 }}>
                <TextArea placeholder="Entegrasyon adresi" style={{ width: '100%' }} maxLength={4000} />
              </Form.Item>
            </td>

            <td style={{ width: '10%', textAlign: 'center' }}>
              <Form.Item shouldUpdate style={{ margin: 0 }}>
                {() => (
                  <Button type="primary" htmlType="submit" disabled={!!form.getFieldsError().filter(({ errors }) => errors.length).length}>
                    Kaydet
                  </Button>
                )}
              </Form.Item>
            </td>
          </tr>
        </tbody>
      </table>
    </Form>
  );
}

export function IntegrationExternalMappingsPage(props: { integration?: Integration }) {
  const [grid, setGrid] = useState<Grid>();
  const [fieldOptions, setFieldOptions] = useState<any>();
  const [gridLoading, setGridLoading] = useState(false);
  const [mappings, setMappings] = useState<IntegrationMapping[]>();
  const [mappingsLoading, setMappingsLoading] = useState(false);

  useEffect(() => {
    setGridLoading(true);
    apiClient
      .get(`grids/${props.integration?.primaryGridId}?includeForeigns=true`)
      .then((response: any) => {
        setGrid(response.data);
        const options = (response.data as Grid).fields.map(f => {
          return { value: f.id, label: f.name };
        });
        setFieldOptions(options);
        setGridLoading(false);
      })
      .catch((reason: any) => {
        setGridLoading(false);
        apiErrorMessage(reason);
      });
  }, [props.integration?.primaryGridId]);

  const loadMappings = useCallback(() => {
    setMappingsLoading(true);
    apiClient
      .get(`mappings?integrationid=${props.integration?.id}`)
      .then(response => {
        let responseData = response.data as IntegrationMapping[];
        responseData = responseData.sort((a1, a2) => (a1.columnName < a2.columnName ? -1 : 1));
        setMappings(responseData);
        setMappingsLoading(false);
      })
      .catch((reason: any) => {
        setMappingsLoading(false);
        apiErrorMessage(reason);
      });
  }, [props.integration?.id]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  if (!grid || !mappings) {
    if (gridLoading || mappingsLoading) {
      return <p>Yükleniyor, lütfen bekleyin.</p>;
    }
    return null;
  }

  return (
    <>
      <table style={{ width: '100%' }}>
        <tbody>
          {mappings?.map((m, i) => (
            <tr key={m.id} className="mapping-row">
              <td style={{ width: '5%', textAlign: 'center' }}>{i + 1}</td>
              <td style={{ width: '30%' }}>{m.columnName}</td>
              <td style={{ width: '35%' }}>
                {m.field.grid.name} / {m.field.name}
              </td>
              <td style={{ width: '20%' }}>
                <IgnoreTasksSwitch mapping={m} onSaved={loadMappings} />
                <IgnoreErrorsSwitch mapping={m} onSaved={loadMappings} />
              </td>
              <td style={{ width: '20%' }}>
                <CommentBox mapping={m} onSaved={loadMappings} />
              </td>
              <td style={{ width: '10%', textAlign: 'center' }}>
                <DeleteButton mapping={m} onSaved={loadMappings} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <NewMappingForm integrationId={props.integration?.id} fieldOptions={fieldOptions} mappings={mappings} onSaved={loadMappings} />
    </>
  );
}
